import * as Sentry from "@sentry/browser";
import debounce from "lodash/debounce";
import { errorText } from "../utils/error-messages";

interface Place {
  title: string;
  lat: number;
  lng: number;
}

interface PlacesResponse {
  data: Place[];
  metadata?: { error_message?: string };
}

const PLACES_URL = "/website-api/area/places/?q=";
export const initAutocomplete = () => {
  // The url value is also swapped by header-search.ts when the search type tab changes,
  // so it doubles as the marker that distinguishes this widget from landing-autocomplete.
  const roots = document.querySelectorAll<HTMLDivElement>(
    ".autocomplete[data-autocomplete-url-value]",
  );

  roots.forEach((root) => {
    setupAutocomplete(root);
  });
};

const setupAutocomplete = (root: HTMLDivElement) => {
  const input = root.querySelector<HTMLInputElement>('[data-autocomplete-el="input"]');
  const loader = root.querySelector<HTMLDivElement>('[data-autocomplete-el="loader"]');
  const resultsContainer = root.querySelector<HTMLDivElement>('[data-autocomplete-el="results"]');
  const addressResults = root.querySelector<HTMLDivElement>(
    '[data-autocomplete-el="address-results"]',
  );
  const nearbyLink = root.querySelector<HTMLAnchorElement>('[data-autocomplete-el="nearby-link"]');
  const searchButton = root.querySelector<HTMLButtonElement>('[data-autocomplete-el="search"]');
  const form = root.querySelector<HTMLFormElement>("form");

  if (!input || !loader || !resultsContainer || !addressResults) {
    return;
  }

  // This component also backs the site-wide header search and the search page's mobile bar
  // (autocompleteId "header-1" / set from Search/search_results.html.twig). Sentry itself is
  // initialized once per page in app.ts, but only the homepage's own instances
  // ("homepage-1"/"homepage-2") report fetch failures, so other Sentry-enabled pages don't
  // also report errors from the header search they render.
  const isHomepageInstance = input.id.startsWith("autocomplete-homepage-");

  let prevController: AbortController | undefined;

  const show = (element: HTMLElement) => element.classList.remove("d-none");
  const hide = (element: HTMLElement) => element.classList.add("d-none");

  // Read lazily: header-search.ts mutates this attribute when the user switches search type tabs.
  const searchUrl = () => root.dataset.autocompleteUrlValue ?? "";

  const showResults = (places: Place[]) => {
    show(resultsContainer);

    let linkCollections = "";

    for (const place of places) {
      const url = `${searchUrl()}?query=${place.title}&lat=${place.lat}&lng=${place.lng}&location_search=address`;

      /* Add tabindex to a element to make relatedTarget work in Safari */
      linkCollections += `<a href="${url}" data-type="href-action" tabindex="0">${place.title}</a>`;
    }

    addressResults.innerHTML = linkCollections;
  };

  const fetchData = (url: string, signal: AbortSignal) => {
    fetch(url, { signal })
      .then((response) => {
        if (response.status === 401) {
          window.location.href = "/";

          return;
        }
        if (response.status === 429) {
          hide(loader);
          alert(errorText("rateLimited"));

          throw new Error("Too many requests");
        }
        if (response.status === 403) {
          hide(loader);

          return response
            .json()
            .then((jsonResponse: PlacesResponse) => {
              const errorMessage = jsonResponse.metadata?.error_message || errorText("proxy");
              alert(errorMessage);
              throw new Error("VPN Detected (403)");
            })
            .catch((_parseError) => {
              // If JSON parsing fails, use default message
              alert(errorText("proxy"));
              throw new Error("VPN Detected (403)");
            });
        }

        if (response.status >= 500) {
          hide(loader);
          alert(errorText("generic"));

          throw new Error(`Places request failed: ${response.status} ${response.statusText}`);
        }

        return response.json();
      })
      .then((jsonResponse: PlacesResponse | undefined) => {
        hide(loader);

        if (!jsonResponse?.data?.length) {
          return;
        }

        showResults(jsonResponse.data);
      })
      .catch((error) => {
        // A new keystroke aborts the previous in-flight request (see prevController.abort()
        // below) — that's expected cancellation, not a failure worth reporting to Sentry.
        if (error?.name === "AbortError") {
          return;
        }

        if (isHomepageInstance) {
          Sentry.captureException(error);
        }
      });
  };

  const showNearbyOption = () => {
    if (nearbyLink) {
      show(nearbyLink);
    }
    show(resultsContainer);
  };

  const goToNearby = (e: Event) => {
    e.preventDefault();

    navigator.geolocation.getCurrentPosition(
      (position) => {
        document.location.href = `${searchUrl()}?lat=${position.coords.latitude}&lng=${position.coords.longitude}&query=${root.dataset.autocompleteNearbyTitleValue ?? ""}&location_search=nearby`;
      },
      (error) => {
        error.code === error.PERMISSION_DENIED
          ? alert(root.dataset.autocompleteNearbyErrorPermissionValue ?? "")
          : alert(root.dataset.autocompleteNearbyErrorValue ?? "");
      },
    );
  };

  // Created once (not per keystroke) so rapid typing collapses into a single fetch once the user
  // pauses, instead of scheduling a separate delayed fetch for every character.
  const debouncedFetch = debounce(fetchData, 400);

  const performSearch = () => {
    const query = input.value.trim();

    if (!query) {
      return;
    }

    if (prevController) {
      prevController.abort();
    }

    const controller = new AbortController();
    prevController = controller;

    show(loader);

    debouncedFetch(`${PLACES_URL}${query}`, controller.signal);
  };

  const goToSearchPage = (e: Event) => {
    e.preventDefault();

    if (input.value?.trim()) {
      hide(resultsContainer);

      document.location.href = `${searchUrl()}?location=${input.value}`;

      return;
    }

    document.location.href = searchUrl();
  };

  const hideResults = (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement | null;

    if (relatedTarget && relatedTarget.dataset.type === "href-action") {
      return;
    }

    hide(resultsContainer);
  };

  input.addEventListener("focus", showNearbyOption);
  input.addEventListener("blur", hideResults);
  input.addEventListener("input", performSearch);

  searchButton?.addEventListener("click", goToSearchPage);
  nearbyLink?.addEventListener("click", goToNearby);
  form?.addEventListener("submit", goToSearchPage);
};
